/**
 * SECURITY: All DB access goes through server-side API - NO getServiceRoleClient() in browser
 */
import { competitionService } from './competitionService';
import { getSessionHeaders } from '../serverSession';

async function callReferralSyncAPI(operation: string, params: any = {}): Promise<any> {
 const response = await fetch('/api/admin/referral-sync', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, ...params }),
 });
 if (!response.ok) {
 const errorData = await response.json().catch(() => ({}));
 throw new Error(errorData.error || `API error: ${response.status}`);
 }
 return response.json();
}

export interface ReferralData {
 userId: number; // The referrer (network leader) ID
 totalReferralVolume: number; // Total USD volume of shares sold through their referral network
 directReferralsCount: number; // Number of people they directly referred
 qualifiedReferralsCount: number; // Number of referrals who bought >= $2,500
}

class ReferralSyncService {

 /**
 * Sync referral data from the bot system to the competition system
 */
 async syncReferralData(): Promise<boolean> {
 try {
 console.log('Starting referral data sync...');

 // Get current active competition
 const competition = await competitionService.getCurrentCompetition();
 if (!competition) {
 console.log('No active competition found, skipping sync');
 return false;
 }

 // Get referral data from the bot system
 const referralData = await this.getReferralDataFromBot();
 if (!referralData || referralData.length === 0) {
 console.log('ℹ No referral data found to sync');
 return true;
 }

 // Update competition participants
 let syncedCount = 0;
 for (const data of referralData) {
 const success = await competitionService.updateParticipant(
 competition.id,
 data.userId,
 data.totalReferralVolume,
 data.directReferralsCount,
 data.qualifiedReferralsCount
 );

 if (success) {
 syncedCount++;
 }
 }

 console.log(`Synced ${syncedCount}/${referralData.length} participants`);
 return true;

 } catch (error) {
 console.error('Error syncing referral data:', error);
 return false;
 }
 }

 /**
 * Get referral data from the bot system
 * This queries the existing referrals and share purchases tables
 */
 private async getReferralDataFromBot(): Promise<ReferralData[]> {
 try {
 // Query to get referral statistics via API
 const data = await callReferralSyncAPI('get_referral_statistics');
 return data || [];
 } catch (error) {
 console.error('Error fetching referral statistics:', error);
 // Fallback: manual calculation via API
 return await this.calculateReferralDataManually();
 }
 }

 /**
 * Manually calculate referral data from existing tables (via API)
 */
 private async calculateReferralDataManually(): Promise<ReferralData[]> {
 try {
 return await callReferralSyncAPI('calculate_referral_data_manually');
 } catch (error) {
 console.error('Error calculating referral data manually:', error);
 return [];
 }
 }

 /**
 * Create the database function for efficient referral statistics calculation
 */
 async createReferralStatisticsFunction(): Promise<boolean> {
 try {
 await callReferralSyncAPI('create_referral_statistics_function');
 console.log('Referral statistics function created successfully');
 return true;
 } catch (error) {
 console.error('Error in createReferralStatisticsFunction:', error);
 return false;
 }
 }

 /**
 * Get leaderboard data for display
 */
 async getLeaderboardData(limit: number = 10) {
 try {
 const competition = await competitionService.getCurrentCompetition();
 if (!competition) {
 return [];
 }

 return await competitionService.getLeaderboard(competition.id, limit);
 } catch (error) {
 console.error('Error getting leaderboard data:', error);
 return [];
 }
 }

 /**
 * Get competition statistics for display
 */
 async getCompetitionStats() {
 try {
 const competition = await competitionService.getCurrentCompetition();
 if (!competition) {
 return {
 totalParticipants: 0,
 qualifiedParticipants: 0,
 leadingVolume: 0,
 totalPrizePool: 0,
 minimumQualification: 2500
 };
 }

 return await competitionService.getCompetitionStats(competition.id);
 } catch (error) {
 console.error('Error getting competition stats:', error);
 return {
 totalParticipants: 0,
 qualifiedParticipants: 0,
 leadingVolume: 0,
 totalPrizePool: 0,
 minimumQualification: 2500
 };
 }
 }

 /**
 * Schedule automatic sync (call this from a cron job or similar)
 */
 async scheduleSync(intervalMinutes: number = 30): Promise<void> {
 console.log(`Scheduling referral sync every ${intervalMinutes} minutes`);
 
 // Initial sync
 await this.syncReferralData();

 // Set up interval
 setInterval(async () => {
 await this.syncReferralData();
 }, intervalMinutes * 60 * 1000);
 }
}

export const referralSyncService = new ReferralSyncService();
